import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import FormLogin from '@auth/components/forms/form-login/form-login.vue';
import { ValidationObserver } from 'vee-validate';
import VueRecaptcha from 'vue-recaptcha';
import BpcHeaderLanguageSelector from '@shared/layouts/components/header/header-language-selector.vue';

@Component({
  name: 'bpc-login',
  components: {
    'bpc-form-login': FormLogin,
    ValidationObserver,
    'vue-recaptcha': VueRecaptcha,
    BpcHeaderLanguageSelector,
  },
})
export default class Login extends Vue {
  $refs!: {
    form: InstanceType<typeof FormLogin> & { login(): void };
    recaptcha: InstanceType<typeof VueRecaptcha> & { reset(): void };
  };

  // @Prop(String) readonly tenantAlias: string;
  get tenantAlias(): string {
    return this.$clientRouter.getTenantAlias();
  }
  sendingLogin: boolean = false;
  recoveringPassword: boolean = false;
  showAlertSuccess: boolean = false;
  showAlertDanger: boolean = false;
  alertMessage: string = '';

  recaptchaVerified: boolean = false;
  recaptchaCode: string = null;

  defaultBackground: string = 'background-color: rgba(15, 58, 93, 0.85)';

  get headerLoginText() {
    return this.customHeaderLoginText
      ? this.customHeaderLoginText
      : this.$t('global.header.login_widget.welcome_back');
  }

  get registerRoute() {
    return {
      name: this.tenantAlias ? 'auth-tenant-register' : 'auth-register',
      params: {
        tenantAlias: this.tenantAlias || null,
      },
    };
  }

  get background() {
    return this.tenantBackground
      ? `background-image: url("${this.tenantBackground}")`
      : this.defaultBackground;
  }

  get logo() {
    return `background-image: url("${this.tenantLogo}")`;
  }

  async beforeMount() {
    if (!this.selectedUserLang && this.lang !== this.defaultLanguage) {
      await this.translationService.getTranslations(this.defaultLanguage);
    }
  }

  login() {
    this.sendingLogin = true;
    this.hideAlert();
    this.$refs.form.login();
  }

  get withRecaptcha(): boolean {
    return !!process.env.VUE_APP_RECAPTCHA_SITE_KEY;
  }

  get sitekey(): string {
    return process.env.VUE_APP_RECAPTCHA_SITE_KEY || '6Le_IMcZAAAAAGFibon8pJAIB809CODlqFdHRowK';
  }

  get lang(): string {
    return this.$store.getters['auth/userLang'] || this.$i18n.locale;
  }

  recoverPassword() {
    this.recoveringPassword = true;
    this.hideAlert();
    if (this.isEmpty(this.$refs.form.formModel['email'])) {
      this.showAlert(
        'danger',
        this.$t('global.header.login_widget.recover_password.email_required'),
      );

      this.recoveringPassword = false;
    } else {
      // Make url with tenant alias or without it
      let url;
      if (this.tenantAlias) {
        url = `auth/${this.tenantAlias}/${this.lang}/password/email`;
      } else {
        url = `${this.lang}/password/email`;
      }

      this.$bpcApi
        .post(url, {
          email: this.$refs.form.formModel['email'],
          recaptcha: this.recaptchaCode,
        })
        .subscribe(
          res => {
            this.showAlert(
              'success',
              this.$t('global.header.login_widget.recover_password.email_sent_successful'),
            );
            this.recoveringPassword = false;
          },
          error => {
            if (error.response.status == '404') {
              this.showAlert(
                'danger',
                this.$t('global.header.login_widget.recover_password.user_not_found'),
              );
              this.recoveringPassword = false;
            }
          },
        );
    }
  }

  showAlert(status, message) {
    this.alertMessage = message;
    this.showAlertSuccess = status === 'success';
    this.showAlertDanger = status === 'danger';
  }

  hideAlert() {
    this.alertMessage = '';
    this.showAlertSuccess = false;
    this.showAlertDanger = false;
  }

  isEmpty(value) {
    return (
      (typeof value == 'string' && !value.trim()) || typeof value == 'undefined' || value === null
    );
  }

  onVerify(response) {
    this.recaptchaCode = response;
    this.recaptchaVerified = true;
  }

  onExpired() {
    this.recaptchaVerified = false;
    this.recaptchaCode = null;
  }

  onResetRecaptcha() {
    this.sendingLogin = false;
    this.recaptchaVerified = false;
    this.recaptchaCode = null;
    this.$refs.recaptcha.reset();
    this.$store.commit('auth/restartedRecaptcha');
  }

  get settingsLoaded() {
    return !this.$store.getters['centerSettings/isLoading'];
  }

  get tenantBackground() {
    if (
      this.$store.getters['centerSettings/centerSettings'] &&
      this.$store.getters['centerSettings/centerSettings'].background
    ) {
      let background = this.$store.getters['centerSettings/centerSettings'].background;
      if (typeof background === 'string') {
        background = JSON.parse(background);
      }
      return background.url;
    }
    return null;
  }
  get tenantLogo() {
    if (
      this.$store.getters['centerSettings/centerSettings'] &&
      this.$store.getters['centerSettings/centerSettings'].logo_url
    ) {
      let logo = this.$store.getters['centerSettings/centerSettings'].logo_url;
      if (logo && typeof logo === 'string') {
        logo = JSON.parse(logo);
      }
      return logo.url;
    }
    return null;
  }
  get customHeaderLoginText() {
    if (this.$store.getters['centerSettings/centerSettings']) {
      return this.$store.getters['centerSettings/centerSettings'].header_login_text;
    }
    return null;
  }
  get availableLanguages() {
    if (this.$store.getters['centerSettings/centerSettings']) {
      return this.$store.getters['centerSettings/centerSettings'].available_languages;
    }
    return null;
  }
  get allowClientRegistration() {
    if (this.$store.getters['centerSettings/centerSettings']) {
      return this.$store.getters['centerSettings/centerSettings'].allow_client_registration;
    }
    return null;
  }
  get selectedUserLang() {
    return this.$store.getters['auth/userLang'];
  }
  get defaultLanguage() {
    if (this.$store.getters['centerSettings/centerSettings']) {
      return this.$store.getters['centerSettings/defaultLanguage'];
    }
    return null;
  }
  get allowAccessPublicWebpage() {
    return this.$store.getters['centerSettings/centerSettings']?.allow_access_public_website;
  }
}
